import { useNewsListQuery } from '@/queries/home/homeQueries'
import { NewsArticle } from '@components/organism/common/NewsArticle.jsx'
import { SectionTitle } from '@components/organism/common/SectionTitle.jsx'
import { formatHHMMDD, formatTimeStringToDate } from '@utils/formatTime.js'
import { useTranslation } from 'react-i18next'
import { MdKeyboardDoubleArrowRight } from 'react-icons/md'
import { Link } from 'react-router-dom'

const NEWS_SECTION_MODE = {
  PAGE: 'page',
  WIDGET: 'widget'
}

const MORE_LINK_POSITION = {
  HEADER: 'header',
  FOOTER: 'footer'
}

const NEWS_SECTION_CONFIG = {
  [NEWS_SECTION_MODE.PAGE]: {
    listPath: '/news',
    detailPath: (key) => `/news/${key}`,
    detailState: undefined,
    showTitleTime: true,
    moreLinkPosition: MORE_LINK_POSITION.FOOTER,
    headerClassName: ''
  },
  [NEWS_SECTION_MODE.WIDGET]: {
    listPath: '/widgets/news/list',
    detailPath: (key) => `/widgets/news/${key}`,
    detailState: { from: '/widgets/news' },
    showTitleTime: false,
    moreLinkPosition: MORE_LINK_POSITION.HEADER,
    headerClassName: 'flex items-center justify-between'
  }
}

const NewsMoreLink = ({ mode, to }) => {
  const { t } = useTranslation()
  if (mode === NEWS_SECTION_MODE.WIDGET) {
    return (
      <Link to={to} className="flex items-center gap-1">
        {t('common.more')}
        <MdKeyboardDoubleArrowRight size={20} />
      </Link>
    )
  }

  return (
    <Link
      to={to}
      className={
        'border-navy-300 text-navy-900 text-16 mt-24 block w-full rounded-t-lg rounded-r-lg border py-14 text-center font-bold'
      }
    >
      {t('common.more')}
    </Link>
  )
}

const NewsSection = ({ isWidget = false }) => {
  const { t } = useTranslation()
  const { data } = useNewsListQuery({ page: 1, count: 5 })

  const mode = isWidget ? NEWS_SECTION_MODE.WIDGET : NEWS_SECTION_MODE.PAGE
  const config = NEWS_SECTION_CONFIG[mode]
  const titleTime = config.showTitleTime ? formatTimeStringToDate(data?.lastFetchTime) : ''
  const moreLink = <NewsMoreLink mode={mode} to={config.listPath} />

  return (
    <section className={'x-spacing y-spacing bg-white'}>
      <div className={config.headerClassName}>
        <SectionTitle title={t('home.news.title')} time={titleTime} />
        {config.moreLinkPosition === MORE_LINK_POSITION.HEADER && moreLink}
      </div>

      <section className={'pt-24'}>
        {Object.entries(data?.newsVOS || {})?.map(([_, { key, pressNm, publishedDt, title }]) => (
          <Link to={config.detailPath(key)} key={key} state={config.detailState}>
            <NewsArticle title={title} pressNm={pressNm} publishedDt={formatHHMMDD(publishedDt)} />
          </Link>
        ))}
      </section>

      {config.moreLinkPosition === MORE_LINK_POSITION.FOOTER && moreLink}
    </section>
  )
}

export default NewsSection
